import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { unitaByte } from '../utils/unita';
import { apiTicketsCreate } from '../utils/api';

// Limiti mostrati e applicati anche qui, non solo sul server.
// Il controllo del server resta quello che conta — questo e' solo cortesia:
// dire subito "questo file non va" invece di far caricare 10 MB per poi
// rifiutarli. Il server non si fida comunque di niente che arrivi da qui.
const MAX_FILE = 3;
const MAX_MB = 10;
const TIPI_AMMESSI = 'image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf';

// The "open a support ticket" panel — docked bottom-left. It has no trigger
// of its own; it's opened via the exposed open()/toggle() ref methods from
// wherever makes sense for the layout (a "Support" link in the header on
// desktop, the "Support" tab in the bottom nav on mobile).
const SupportButton = forwardRef(function SupportButton({ sidebarOpen }, ref) {
  const { t } = useTranslation('widgets');
  const [open, setOpen]       = useState(false);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy]       = useState(false);
  const [sentCode, setSentCode] = useState(null);
  const [error, setError]     = useState('');
  const [files, setFiles]     = useState([]);
  const inputFile             = useRef(null);

  useImperativeHandle(ref, () => ({
    open: () => { setOpen(true); setError(''); },
    toggle: () => { setOpen(o => !o); setError(''); },
  }));

  function reset() {
    setOpen(false);
    setSentCode(null);
    setSubject('');
    setMessage('');
    setError('');
    setFiles([]);
    if (inputFile.current) inputFile.current.value = '';
  }

  // Messaggi in parole, non codici: chi scrive all'assistenza sta gia' avendo
  // un problema, non deve riceverne un secondo scritto in gergo.
  function aggiungiFile(e) {
    const scelti = Array.from(e.target.files || []);
    if (!scelti.length) return;
    const insieme = [...files, ...scelti];
    if (insieme.length > MAX_FILE) {
      setError(t('support_att_too_many', { max: MAX_FILE }));
      e.target.value = '';
      return;
    }
    const troppoGrande = scelti.find(f => f.size > MAX_MB * 1024 * 1024);
    if (troppoGrande) {
      setError(t('support_att_too_big', { name: troppoGrande.name, max: MAX_MB }));
      e.target.value = '';
      return;
    }
    setError('');
    setFiles(insieme);
    e.target.value = '';
  }

  function togliFile(i) {
    setFiles(files.filter((_, k) => k !== i));
  }

  function pesoLeggibile(b) {
    const lingua = document.documentElement.lang;
    return b < 1024 * 1024 ? `${Math.round(b / 1024)} ${unitaByte(lingua, 'KB')}` : `${(b / 1024 / 1024).toFixed(1)} ${unitaByte(lingua, 'MB')}`;
  }

  async function handleSend(e) {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) return;
    setBusy(true); setError('');
    try {
      const { ticket } = await apiTicketsCreate(subject.trim(), message.trim(), files);
      setSentCode(ticket.code);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }

  return (
    <div className={`support-fab-wrap ${sidebarOpen ? 'with-sidebar' : ''}`}>
      {open && (
        <div className="support-panel">
          <div className="support-panel-header">
            <span>{t('support_title')}</span>
            <button className="support-panel-close" onClick={reset} title={t('support_close')}>✕</button>
          </div>

          {sentCode ? (
            <div className="support-panel-sent">
              <p>✓ {t('support_sent_prefix')} <strong>{sentCode}</strong> {t('support_sent_suffix')}</p>
              <button className="btn-enable-2fa" onClick={reset}>{t('support_done')}</button>
            </div>
          ) : (
            <form onSubmit={handleSend}>
              <input
                className="tf-input" placeholder={t('support_subject_placeholder')} maxLength={200}
                value={subject} onChange={e => setSubject(e.target.value)} autoFocus
              />
              <textarea
                className="ticket-textarea" placeholder={t('support_message_placeholder')} maxLength={5000}
                value={message} onChange={e => setMessage(e.target.value)}
              />
              {/* Tipi e limite detti PRIMA di scegliere, non dopo il rifiuto. */}
              <div className="ticket-att-row">
                <button type="button" className="ticket-att-add" onClick={() => inputFile.current?.click()}>
                  📎 {t('support_att_add')}
                </button>
                <span className="ticket-att-hint">{t('support_att_hint', { max: MAX_FILE, mb: MAX_MB })}</span>
                <input
                  ref={inputFile} type="file" multiple accept={TIPI_AMMESSI}
                  style={{ display: 'none' }} onChange={aggiungiFile}
                />
              </div>
              {files.length > 0 && (
                <ul className="ticket-att-list">
                  {files.map((f, i) => (
                    <li key={`${f.name}-${i}`}>
                      <span className="ticket-att-name">{f.name}</span>
                      <span className="ticket-att-size">{pesoLeggibile(f.size)}</span>
                      <button type="button" className="ticket-att-del" onClick={() => togliFile(i)}
                              title={t('support_att_remove')}>✕</button>
                    </li>
                  ))}
                </ul>
              )}
              {error && <div className="profile-msg error">⚠ {error}</div>}
              <button type="submit" className="btn-enable-2fa" disabled={busy || !subject.trim() || !message.trim()}>
                {busy ? t('support_sending') : t('support_submit')}
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
});

export default SupportButton;
